import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ModuleProps } from '../types';
import AppHeader from '../AppHeader';
import { useLanguage } from '../LanguageContext';
import { el as elM, en as enM } from '../i18n/modules';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Comment {
  id: string;
  text: string;
  author: string;
  author_initials: string;
  created_at: string;
}

interface Attachment {
  id: string;
  file_name: string;
  file_path: string;
  mime_type: string;
  size: number;
  uploaded_by: string;
  uploaded_at: string;
}

interface DailyLogTask {
  id: string;
  title: string;
  description: string;
  parent_work_package: string;
  activity_id: string;
  activity_title: string;
  assignee: string;
  status: 'Open' | 'In Progress' | 'Done';
  is_critical: boolean;
  created_date: string;
  due_date: string | null;
  comments: Comment[];
  attachments: Attachment[];
  created_by_name: string;
}

const COLUMNS: { key: DailyLogTask['status']; label: string; color: string; dot: string }[] = [
  { key: 'Open', label: 'Open', color: '#f3f4f6', dot: '#9ca3af' },
  { key: 'In Progress', label: 'In Progress', color: '#fffbeb', dot: '#f59e0b' },
  { key: 'Done', label: 'Done', color: '#f0fdf4', dot: '#22c55e' },
];

const getInitials = (name: string) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
};

const avatarColors = [
  { bg: '#e0e7ff', color: '#4f46e5' },
  { bg: '#dcfce7', color: '#166534' },
  { bg: '#fce7f3', color: '#9d174d' },
  { bg: '#fef9c3', color: '#854d0e' },
  { bg: '#dbeafe', color: '#1e40af' },
];

const getAvatarColor = (name: string) => avatarColors[name.charCodeAt(0) % avatarColors.length];

const getDaysRemaining = (dueDate: string | null) => {
  if (!dueDate) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate.split('T')[0] + 'T00:00:00');
  return Math.floor((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

const getFileIcon = (mimeType: string, fileName: string): string => {
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType === 'application/pdf') return '📄';
  if (mimeType.includes('word') || fileName.endsWith('.docx') || fileName.endsWith('.doc')) return '📝';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) return '📊';
  if (mimeType.includes('presentation') || fileName.endsWith('.pptx') || fileName.endsWith('.ppt')) return '📋';
  if (mimeType.includes('zip') || mimeType.includes('compressed')) return '🗜️';
  return '📁';
};

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DailyLog: React.FC<ModuleProps> = ({ project, user, token, onBack, canEdit, onLogout, onProfileEdit, openCreate, openTaskId }) => {
  const { language } = useLanguage();
  const M = language === 'en' ? enM : elM;
  const L = M.dailyLog;
  const C = M.common;
  const [tasks, setTasks] = useState<DailyLogTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<DailyLogTask | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [members, setMembers] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [filterAssignee, setFilterAssignee] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterSearch, setFilterSearch] = useState<string>('');
  const [filterCritical, setFilterCritical] = useState<string>('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [attachToDelete, setAttachToDelete] = useState<Attachment | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);

  const emptyForm = {
    title: '', description: '', parent_work_package: '',
    activity_id: '', activity_title: '', assignee: '',
    status: 'Open' as DailyLogTask['status'], due_date: '',
    is_critical: false,
  };
  const [form, setForm] = useState({ ...emptyForm });
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (openCreate && canEdit) setShowModal(true); }, [openCreate]);

  const fetchAll = async () => {
    try {
      const [logsRes, stakeholdersRes, scheduleRes] = await Promise.allSettled([
        axios.get(`${API}/daily-log/${project.id}`, { headers }),
        axios.get(`${API}/modules/${project.id}/Stakeholders`, { headers }),
        axios.get(`${API}/modules/${project.id}/Schedule`, { headers }),
      ]);
      if (logsRes.status === 'fulfilled') setTasks(logsRes.value.data || []);
      if (stakeholdersRes.status === 'fulfilled') {
        const stakeholders: any[] = stakeholdersRes.value.data?.data?.stakeholders || [];
        setMembers(stakeholders.map(s => ({ id: s.id, name: s.name, role: s.role || '', contact: s.contact || '' })));
      }
      if (scheduleRes.status === 'fulfilled') {
        const data = scheduleRes.value.data?.data;
        setActivities([...(data?.activities || []), ...(data?.milestones || [])]);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.title.trim()) return;
    try {
      const res = await axios.post(`${API}/daily-log/${project.id}`, {
        ...form,
        created_by_name: user.full_name,
        activity_title: activities.find(a => a.id === form.activity_id)?.title || '',
        assignee_email: members.find(m => m.name === form.assignee)?.contact || '',
      }, { headers });
      setTasks(prev => [res.data, ...prev]);
      setShowModal(false);
      setForm({ ...emptyForm });
    } catch (err) { console.error(err); }
  };

  const handleUpdate = async () => {
    if (!selectedTask) return;
    try {
      const res = await axios.put(`${API}/daily-log/${project.id}/${selectedTask.id}`, {
        ...form,
        activity_title: activities.find(a => a.id === form.activity_id)?.title || form.activity_title,
        assignee_email: members.find(m => m.name === form.assignee)?.contact || '',
      }, { headers });
      setTasks(prev => prev.map(t => t.id === selectedTask.id ? res.data : t));
      setSelectedTask(res.data);
      setIsEditing(false);
    } catch (err) { console.error(err); }
  };

  const handleStatusChange = async (id: string, status: DailyLogTask['status']) => {
    try {
      const res = await axios.put(`${API}/daily-log/${project.id}/${id}/status`, { status }, { headers });
      setTasks(prev => prev.map(t => t.id === id ? res.data : t));
      if (selectedTask?.id === id) setSelectedTask(res.data);
    } catch (err) { console.error(err); }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTask) return;
    setSavingComment(true);
    try {
      const res = await axios.post(`${API}/daily-log/${project.id}/${selectedTask.id}/comment`, {
        text: newComment, author: user.full_name, author_initials: getInitials(user.full_name),
      }, { headers });
      setTasks(prev => prev.map(t => t.id === selectedTask.id ? res.data : t));
      setSelectedTask(res.data);
      setNewComment('');
    } catch (err) { console.error(err); }
    finally { setSavingComment(false); }
  };

  const handleDelete = (id: string) => setTaskToDelete(id);

  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    try {
      await axios.delete(`${API}/daily-log/${project.id}/${taskToDelete}`, { headers });
      setTasks(prev => prev.filter(t => t.id !== taskToDelete));
      setSelectedTask(null);
    } catch (err) { console.error(err); }
    finally { setTaskToDelete(null); }
  };

  const handleUploadAttachment = async (file: File) => {
    if (!selectedTask) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/daily-log/${project.id}/${selectedTask.id}/attachment`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setTasks(prev => prev.map(t => t.id === selectedTask.id ? res.data : t));
      setSelectedTask(res.data);
    } catch (err) { console.error(err); }
    finally { setUploadingFile(false); }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!selectedTask) return;
    try {
      const res = await axios.delete(`${API}/daily-log/${project.id}/${selectedTask.id}/attachment/${attachmentId}`, { headers });
      setTasks(prev => prev.map(t => t.id === selectedTask.id ? res.data : t));
      setSelectedTask(res.data);
      setAttachToDelete(null);
    } catch (err) { console.error(err); }
  };

  const handleDownloadAttachment = async (attachmentId: string) => {
    if (!selectedTask) return;
    try {
      const res = await axios.get(`${API}/daily-log/${project.id}/${selectedTask.id}/attachment/${attachmentId}/url`, { headers });
      window.open(res.data.url, '_blank');
    } catch (err) { console.error(err); }
  };

  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    if (!canEdit) return;
    e.dataTransfer.setData('taskId', taskId);
  };

  const handleDrop = async (e: React.DragEvent, status: DailyLogTask['status']) => {
    if (!canEdit) return;
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    const task = tasks.find(t => t.id === taskId);
    if (task && task.status !== status) await handleStatusChange(taskId, status);
    setDragOver(null);
  };

  const openTask = (task: DailyLogTask) => {
    setSelectedTask(task);
    setForm({
      title: task.title, description: task.description || '',
      parent_work_package: task.parent_work_package || '',
      activity_id: task.activity_id || '', activity_title: task.activity_title || '',
      assignee: task.assignee || '', status: task.status, due_date: task.due_date || '',
      is_critical: task.is_critical || false,
    });
    setIsEditing(false);
  };

  const openedFromNotificationRef = React.useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!openTaskId || loading) return;
    if (openedFromNotificationRef.current === openTaskId) return;
    const task = tasks.find(t => t.id === openTaskId);
    if (task) {
      openedFromNotificationRef.current = openTaskId;
      openTask(task);
    }
  }, [openTaskId, loading, tasks]);

  const formatDate = (d: string) => new Date(d).toLocaleDateString(L.dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  const formatDateTime = (d: string) => new Date(d).toLocaleString(L.dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const DaysRemainingBadge = ({ dueDate, status }: { dueDate: string | null; status: string }) => {
    if (!dueDate || status === 'Done') return null;
    const days = getDaysRemaining(dueDate);
    if (days === null) return null;
    if (days < 0) return <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', backgroundColor: '#fee2e2', color: '#991b1b', fontWeight: '600' }}>Overdue</span>;
    if (days === 0) return <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', backgroundColor: '#fee2e2', color: '#991b1b', fontWeight: '600' }}>{L.daysToday}</span>;
    if (days <= 3) return <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', backgroundColor: '#fef9c3', color: '#854d0e', fontWeight: '500' }}>{days} {L.daysAbbr}</span>;
    return <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', backgroundColor: '#dbeafe', color: '#1e40af' }}>{days} {L.daysAbbr}</span>;
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      {/* Header */}
      <AppHeader user={user} onHome={onBack} onProfileEdit={onProfileEdit} onLogout={onLogout} />

      {/* Breadcrumb */}
      <div style={{ padding: '16px 32px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', fontSize: '14px', position: 'sticky', top: 64, zIndex: 99 }}>
        <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: '500' }} onClick={onBack}>{C.myProjects}</span>
        <span style={{ color: '#9ca3af', margin: '0 8px' }}>/</span>
        <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: '500' }} onClick={onBack}>{project.name}</span>
        <span style={{ color: '#9ca3af', margin: '0 8px' }}>/</span>
        <span style={{ color: '#374151', fontWeight: '500' }}>Task Tracker</span>
      </div>

      <div style={{ padding: '28px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h2 style={{ fontSize: '22px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 4px' }}>Task Tracker</h2>
            <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>{L.subtitle}</p>
          </div>
          {canEdit && (
            <button onClick={() => { setShowModal(true); setForm({ ...emptyForm }); }} style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
              {L.btnNew}
            </button>
          )}
        </div>

        {/* Filter bar */}
        {!loading && tasks.length > 0 && (() => {
          const uniqueAssignees = tasks.map(t => t.assignee).filter(Boolean).filter((a, i, arr) => arr.indexOf(a) === i).sort();
          const hasFilters = filterAssignee || filterStatus || filterSearch || filterCritical;
          const selectStyle: React.CSSProperties = {
            padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb',
            fontSize: 13, color: '#374151', background: 'white',
            cursor: 'pointer', outline: 'none', fontFamily: 'inherit', minWidth: 160,
          };
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: filterSearch ? '#4f46e5' : '#9ca3af', fontSize: 13, pointerEvents: 'none' }}>🔍</span>
                <input
                  type="text"
                  value={filterSearch}
                  onChange={e => setFilterSearch(e.target.value)}
                  placeholder="Search tasks..."
                  style={{ ...selectStyle, paddingLeft: 30, minWidth: 200, borderColor: filterSearch ? '#4f46e5' : '#e5e7eb', background: filterSearch ? '#ede9fe' : 'white', color: filterSearch ? '#4f46e5' : '#374151' }}
                />
              </div>
              <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)} style={{ ...selectStyle, borderColor: filterAssignee ? '#4f46e5' : '#e5e7eb', background: filterAssignee ? '#ede9fe' : 'white', color: filterAssignee ? '#4f46e5' : '#374151' }}>
                <option value="">{L.filterAllAssignees}</option>
                {uniqueAssignees.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...selectStyle, borderColor: filterStatus ? '#4f46e5' : '#e5e7eb', background: filterStatus ? '#ede9fe' : 'white', color: filterStatus ? '#4f46e5' : '#374151' }}>
                <option value="">{L.filterAllStatuses}</option>
                {COLUMNS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              <select value={filterCritical} onChange={e => setFilterCritical(e.target.value)} style={{ ...selectStyle, borderColor: filterCritical ? '#ef4444' : '#e5e7eb', background: filterCritical ? '#fee2e2' : 'white', color: filterCritical ? '#991b1b' : '#374151' }}>
                <option value="">{L.filterAllCritical}</option>
                <option value="critical">{L.filterCriticalOnly}</option>
                <option value="non-critical">{L.filterNonCriticalOnly}</option>
              </select>
              {hasFilters && (
                <button onClick={() => { setFilterAssignee(''); setFilterStatus(''); setFilterSearch(''); setFilterCritical(''); }}
                  style={{ fontSize: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', fontFamily: 'inherit' }}>
                  {L.filterClear}
                </button>
              )}
            </div>
          );
        })()}

        {loading ? (
          <p style={{ color: '#666' }}>{C.loading}</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', alignItems: 'start' }}>
            {COLUMNS.map(col => {
              const colTasks = tasks
                .filter(t => t.status === col.key)
                .filter(t => !filterAssignee || t.assignee === filterAssignee)
                .filter(t => !filterStatus || t.status === filterStatus)
                .filter(t => !filterSearch || t.title.toLowerCase().includes(filterSearch.toLowerCase()))
                .filter(t => !filterCritical || (filterCritical === 'critical' ? t.is_critical : !t.is_critical));
              return (
                <div
                  key={col.key}
                  onDragOver={e => { if (!canEdit) return; e.preventDefault(); setDragOver(col.key); }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={e => handleDrop(e, col.key)}
                  style={{ backgroundColor: dragOver === col.key ? '#ede9fe' : '#f8fafc', borderRadius: '12px', padding: '14px', border: dragOver === col.key ? '2px dashed #4f46e5' : '2px dashed transparent', transition: 'all 0.15s' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: col.dot }} />
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{col.label}</span>
                    <span style={{ marginLeft: 'auto', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '999px', fontSize: '11px', padding: '1px 8px', color: '#6b7280' }}>{colTasks.length}</span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {colTasks.map(task => {
                      const av = getAvatarColor(task.assignee || '');
                      return (
                        <div
                          key={task.id}
                          draggable={canEdit}
                          onDragStart={e => handleDragStart(e, task.id)}
                          onClick={() => openTask(task)}
                          style={{ backgroundColor: 'white', border: '1px solid #f3f4f6', borderLeft: `3px solid ${col.dot}`, borderRadius: '8px', padding: '12px', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                          onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)')}
                          onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '6px' }}>
                            {task.is_critical && (
                              <span title={L.fieldCriticalDesc} style={{ flexShrink: 0, width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#ef4444', color: 'white', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '1px' }}>!</span>
                            )}
                            <p style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e', margin: 0, lineHeight: '1.4' }}>{task.title}</p>
                          </div>
                          {task.activity_title && <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 8px' }}>↳ {task.activity_title}</p>}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {task.assignee ? (
                                <>
                                  <div style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: av.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '600', color: av.color }}>{getInitials(task.assignee)}</div>
                                  <span style={{ fontSize: '11px', color: '#6b7280' }}>{task.assignee.split(' ')[0]}</span>
                                </>
                              ) : (
                                <span style={{ fontSize: '11px', color: '#d1d5db' }}>{L.noAssignee}</span>
                              )}
                            </div>
                            <DaysRemainingBadge dueDate={task.due_date} status={task.status} />
                          </div>
                          {task.comments?.length > 0 && <div style={{ marginTop: '8px', fontSize: '11px', color: '#9ca3af' }}>💬 {task.comments.length} {L.commentsLabel}</div>}
                        </div>
                      );
                    })}
                    {colTasks.length === 0 && (
                      <div style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: '#d1d5db', border: '1px dashed #e5e7eb', borderRadius: '8px' }}>
                        {canEdit ? L.dragHere : 'Κενό'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showModal && canEdit && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '28px', width: '560px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a2e', margin: 0 }}>{L.modalNewTitle}</h2>
              <button onClick={() => setShowModal(false)} style={{ backgroundColor: 'transparent', border: 'none', fontSize: '18px', color: '#9ca3af', cursor: 'pointer' }}>✕</button>
            </div>
            <TaskForm form={form} setForm={setForm} members={members} activities={activities} />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
              <button onClick={() => setShowModal(false)} style={{ backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '8px 18px', borderRadius: '7px', fontSize: '13px', cursor: 'pointer' }}>{C.cancel}</button>
              <button onClick={handleCreate} disabled={!form.title.trim()} style={{ backgroundColor: form.title.trim() ? '#4f46e5' : '#e5e7eb', color: form.title.trim() ? 'white' : '#9ca3af', border: 'none', padding: '8px 18px', borderRadius: '7px', fontSize: '13px', fontWeight: '600', cursor: form.title.trim() ? 'pointer' : 'not-allowed' }}>
                {L.btnCreate}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attachment Delete Confirmation */}
      {attachToDelete && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '28px 32px', width: '400px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#991b1b', flexShrink: 0, fontSize: '18px' }}>🗑</div>
              <div>
                <p style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 4px' }}>{L.deleteFileTitle}</p>
                <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>{L.deleteFileText}</p>
              </div>
            </div>
            <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '12px 14px', marginBottom: '20px', border: '1px solid #e5e7eb' }}>
              <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 4px' }}>{L.deleteFileLabel}</p>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a2e', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{attachToDelete.file_name}</p>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setAttachToDelete(null)} style={{ backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '9px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>{C.cancel}</button>
              <button onClick={() => handleDeleteAttachment(attachToDelete.id)} style={{ backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', padding: '9px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>{L.deleteFileTitle}</button>
            </div>
          </div>
        </div>
      )}

      {/* Task Delete Confirmation */}
      {taskToDelete && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '28px 32px', width: '400px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#991b1b', flexShrink: 0, fontSize: '18px' }}>🗑</div>
              <div>
                <p style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 4px' }}>{L.confirmDeleteTask}</p>
                <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>{tasks.find(t => t.id === taskToDelete)?.title}</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setTaskToDelete(null)} style={{ backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '9px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>{C.cancel}</button>
              <button onClick={confirmDeleteTask} style={{ backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', padding: '9px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>{C.delete}</button>
            </div>
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      {selectedTask && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '640px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '20px 24px 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div style={{ flex: 1 }}>
                {!isEditing ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', backgroundColor: COLUMNS.find(c => c.key === selectedTask.status)?.color || '#f3f4f6', color: '#374151', fontWeight: '500', border: '1px solid #e5e7eb' }}>{selectedTask.status}</span>
                      {selectedTask.is_critical && (
                        <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', backgroundColor: '#fee2e2', color: '#991b1b', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: '#ef4444', color: 'white', fontSize: '10px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>!</span>
                          {L.fieldCritical}
                        </span>
                      )}
                      {selectedTask.activity_title && <span style={{ fontSize: '11px', color: '#9ca3af' }}>↳ {selectedTask.activity_title}</span>}
                    </div>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 4px' }}>{selectedTask.title}</h2>
                    <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>{L.createdBy} {formatDate(selectedTask.created_date)} από {selectedTask.created_by_name || '—'}</p>
                  </>
                ) : (
                  <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a2e', margin: 0 }}>{L.modalEditTitle}</h2>
                )}
              </div>
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginLeft: '12px' }}>
                {!isEditing && canEdit && (
                  <button onClick={() => setIsEditing(true)} style={{ fontSize: '12px', padding: '5px 12px', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: 'white', color: '#374151', cursor: 'pointer' }}>{L.btnEdit}</button>
                )}
                {canEdit && (
                  <button onClick={() => handleDelete(selectedTask.id)} style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '6px', border: '1px solid #fee2e2', backgroundColor: 'white', color: '#ef4444', cursor: 'pointer' }}>🗑</button>
                )}
                <button onClick={() => { setSelectedTask(null); setIsEditing(false); }} style={{ backgroundColor: 'transparent', border: 'none', fontSize: '18px', color: '#9ca3af', cursor: 'pointer' }}>✕</button>
              </div>
            </div>

            <div style={{ padding: '16px 24px 24px' }}>
              {isEditing && canEdit ? (
                <>
                  <TaskForm form={form} setForm={setForm} members={members} activities={activities} />
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
                    <button onClick={() => setIsEditing(false)} style={{ backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '8px 18px', borderRadius: '7px', fontSize: '13px', cursor: 'pointer' }}>{C.cancel}</button>
                    <button onClick={handleUpdate} style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '8px 18px', borderRadius: '7px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>{C.save}</button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                    <InfoField label={L.fieldAssignee} value={selectedTask.assignee || '—'} />
                    <InfoField label={L.fieldStatus}>
                      {canEdit ? (
                        <select value={selectedTask.status} onChange={e => handleStatusChange(selectedTask.id, e.target.value as DailyLogTask['status'])} style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: 'white', cursor: 'pointer' }}>
                          <option>Open</option>
                          <option>In Progress</option>
                          <option>Done</option>
                        </select>
                      ) : (
                        <span style={{ fontSize: '13px', color: '#374151' }}>{selectedTask.status}</span>
                      )}
                    </InfoField>
                    <InfoField label={L.fieldCompletionDate} value={selectedTask.due_date ? formatDate(selectedTask.due_date) : '—'} />
                    <InfoField label={L.fieldDeadline}><DaysRemainingBadge dueDate={selectedTask.due_date} status={selectedTask.status} /></InfoField>
                    {selectedTask.parent_work_package && <InfoField label="Work Package" value={selectedTask.parent_work_package} />}
                    {selectedTask.activity_title && <InfoField label="Activity" value={selectedTask.activity_title} />}
                  </div>

                  {selectedTask.description && (
                    <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                      <p style={{ fontSize: '12px', fontWeight: '600', color: '#9ca3af', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{L.fieldDescription}</p>
                      <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: '1.6' }}>{selectedTask.description}</p>
                    </div>
                  )}

                  {/* Attachments */}
                  <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '16px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', margin: 0 }}>{L.fieldAttachments} ({selectedTask.attachments?.length || 0})</p>
                      {canEdit && (
                        <div>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
                            style={{ display: 'none' }}
                            onChange={e => { if (e.target.files?.[0]) { handleUploadAttachment(e.target.files[0]); e.target.value = ''; } }}
                          />
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingFile}
                            style={{ fontSize: '12px', padding: '4px 12px', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: 'white', color: '#374151', cursor: uploadingFile ? 'not-allowed' : 'pointer', opacity: uploadingFile ? 0.6 : 1 }}
                          >
                            {uploadingFile ? L.uploadingBtn : L.uploadBtn}
                          </button>
                        </div>
                      )}
                    </div>
                    {selectedTask.attachments?.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {selectedTask.attachments.map(att => (
                          <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #f3f4f6' }}>
                            <span style={{ fontSize: '16px', flexShrink: 0 }}>{getFileIcon(att.mime_type, att.file_name)}</span>
                            <span
                              onClick={() => handleDownloadAttachment(att.id)}
                              style={{ fontSize: '13px', color: '#4f46e5', cursor: 'pointer', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'underline' }}
                              title={att.file_name}
                            >
                              {att.file_name}
                            </span>
                            <span style={{ fontSize: '11px', color: '#9ca3af', flexShrink: 0 }}>{formatFileSize(att.size)}</span>
                            {canEdit && (
                              <button onClick={() => setAttachToDelete(att)} style={{ background: 'none', border: 'none', fontSize: '14px', color: '#d1d5db', cursor: 'pointer', flexShrink: 0, padding: '0 2px', lineHeight: 1 }} title={C.deleteTip}>✕</button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>{L.noAttachments}</p>
                    )}
                  </div>

                  {/* Comments */}
                  <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', margin: '0 0 12px' }}>{L.fieldComments} ({selectedTask.comments?.length || 0})</p>
                    {selectedTask.comments?.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px', maxHeight: '200px', overflowY: 'auto' }}>
                        {[...selectedTask.comments].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()).map(c => {
                          const av = getAvatarColor(c.author);
                          return (
                            <div key={c.id} style={{ display: 'flex', gap: '10px' }}>
                              <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: av.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '600', color: av.color, flexShrink: 0 }}>{c.author_initials || getInitials(c.author)}</div>
                              <div style={{ flex: 1, backgroundColor: '#f9fafb', borderRadius: '8px', padding: '10px 12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>{c.author}</span>
                                  <span style={{ fontSize: '11px', color: '#9ca3af' }}>{formatDateTime(c.created_at)}</span>
                                </div>
                                <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: '1.5' }}>{c.text}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '12px' }}>{L.noComments}</p>
                    )}

                    {/* Το comment box εμφανίζεται για ΟΛΟΥΣ — και read-only χρήστες μπορούν να σχολιάσουν */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <textarea
                        value={newComment}
                        onChange={e => setNewComment(e.target.value)}
                        placeholder={L.commentPlaceholder}
                        style={{ flex: 1, padding: '8px 10px', borderRadius: '7px', border: '1px solid #e5e7eb', fontSize: '13px', resize: 'none', height: '60px', outline: 'none', fontFamily: 'inherit' }}
                      />
                      <button onClick={handleAddComment} disabled={!newComment.trim() || savingComment} style={{ backgroundColor: newComment.trim() ? '#4f46e5' : '#e5e7eb', color: newComment.trim() ? 'white' : '#9ca3af', border: 'none', padding: '8px 14px', borderRadius: '7px', fontSize: '13px', fontWeight: '600', cursor: newComment.trim() ? 'pointer' : 'not-allowed', alignSelf: 'flex-end' }}>
                        {savingComment ? '...' : L.commentAdd}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoField = ({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) => (
  <div>
    <p style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', margin: '0 0 3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</p>
    {children || <p style={{ fontSize: '13px', color: '#374151', margin: 0 }}>{value}</p>}
  </div>
);

const TaskForm = ({ form, setForm, members, activities }: { form: any; setForm: any; members: any[]; activities: any[] }) => {
  const { language } = useLanguage();
  const M = language === 'en' ? enM : elM;
  const L = M.dailyLog;
  const todayLocal = new Date(); todayLocal.setHours(0, 0, 0, 0);
  const today = `${todayLocal.getFullYear()}-${String(todayLocal.getMonth()+1).padStart(2,'0')}-${String(todayLocal.getDate()).padStart(2,'0')}`;
  const daysRemaining = form.due_date ? getDaysRemaining(form.due_date) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '5px' }}>{L.fieldTitle}</label>
        <input style={{ width: '100%', padding: '9px 10px', borderRadius: '7px', border: '1px solid #e5e7eb', fontSize: '13px', boxSizing: 'border-box' as const, outline: 'none' }} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder={L.fieldTitlePlaceholder} />
      </div>
      <div>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '5px' }}>{L.fieldDescription}</label>
        <textarea style={{ width: '100%', padding: '9px 10px', borderRadius: '7px', border: '1px solid #e5e7eb', fontSize: '13px', boxSizing: 'border-box' as const, outline: 'none', resize: 'none', height: '72px', fontFamily: 'inherit' }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder={L.fieldDescPlaceholder} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '5px' }}>{L.fieldWorkPackage}</label>
          <input style={{ width: '100%', padding: '9px 10px', borderRadius: '7px', border: '1px solid #e5e7eb', fontSize: '13px', boxSizing: 'border-box' as const, outline: 'none' }} value={form.parent_work_package} onChange={e => setForm({ ...form, parent_work_package: e.target.value })} placeholder={L.fieldWorkPackagePlaceholder} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '5px' }}>{L.fieldActivity}</label>
          <select style={{ width: '100%', padding: '9px 10px', borderRadius: '7px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', backgroundColor: 'white' }} value={form.activity_id} onChange={e => setForm({ ...form, activity_id: e.target.value })}>
            <option value="">{L.fieldActivityPlaceholder}</option>
            {activities.map(a => <option key={a.id} value={a.id}>{a.wbs_code} {a.title}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '5px' }}>{L.fieldAssignee}</label>
          <select style={{ width: '100%', padding: '9px 10px', borderRadius: '7px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', backgroundColor: 'white' }} value={form.assignee} onChange={e => setForm({ ...form, assignee: e.target.value })}>
            <option value="">{L.fieldAssigneePlaceholder}</option>
            {members.map(m => <option key={m.id} value={m.name}>{m.name} — {m.role}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '5px' }}>{L.fieldStatus}</label>
          <select style={{ width: '100%', padding: '9px 10px', borderRadius: '7px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', backgroundColor: 'white' }} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
            <option>Open</option>
            <option>In Progress</option>
            <option>Done</option>
          </select>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '5px' }}>{L.fieldCreationDate}</label>
          <input style={{ width: '100%', padding: '9px 10px', borderRadius: '7px', border: '1px solid #e5e7eb', fontSize: '13px', boxSizing: 'border-box' as const, outline: 'none', backgroundColor: '#f9fafb', color: '#9ca3af' }} value={today} disabled />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '5px' }}>{L.fieldCompletionDate}</label>
          <input type="date" style={{ width: '100%', padding: '9px 10px', borderRadius: '7px', border: '1px solid #e5e7eb', fontSize: '13px', boxSizing: 'border-box' as const, outline: 'none' }} value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
        </div>
      </div>
      <div
        onClick={() => setForm({ ...form, is_critical: !form.is_critical })}
        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '8px', border: `1px solid ${form.is_critical ? '#fca5a5' : '#e5e7eb'}`, backgroundColor: form.is_critical ? '#fff1f2' : '#f9fafb', cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: form.is_critical ? '#ef4444' : '#e5e7eb', color: 'white', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>!</div>
        <span style={{ fontSize: '13px', fontWeight: '500', color: form.is_critical ? '#991b1b' : '#6b7280' }}>{L.fieldCritical}</span>
        <span style={{ fontSize: '12px', color: form.is_critical ? '#ef4444' : '#9ca3af', marginLeft: 'auto' }}>{form.is_critical ? '✓ Ενεργό' : L.fieldCriticalDesc}</span>
      </div>
      {form.due_date && (
        <div style={{ backgroundColor: daysRemaining !== null && daysRemaining < 0 ? '#fee2e2' : '#f0fdf4', borderRadius: '8px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: daysRemaining !== null && daysRemaining < 0 ? '#ef4444' : daysRemaining !== null && daysRemaining <= 3 ? '#f59e0b' : '#22c55e' }} />
          {daysRemaining !== null && daysRemaining < 0 ? (
            <span style={{ fontSize: '13px', color: '#991b1b', fontWeight: '600' }}>{L.overdueMsg(Math.abs(daysRemaining))}</span>
          ) : daysRemaining === 0 ? (
            <span style={{ fontSize: '13px', color: '#991b1b', fontWeight: '600' }}>{L.dueTodayMsg}</span>
          ) : (
            <span style={{ fontSize: '13px', color: '#166534' }}>{L.daysLeftMsg(daysRemaining)}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default DailyLog;
